import type { AppDispatch } from '@/store/store'
import { fetchAndStoreUserFiles } from '@/user_files/files/utils/fetch-and-store-user-files'
import { ArtifactTemplatesActions } from '@/artifact-templates/store/slice'
import type { MatterArtifactTemplateSelectionRead, MatterArtifactAppliedTemplateRead } from '@/artifact-templates/schemas'

export type HydrateAgentV2ConversationTemplateStateArgs = {
  selections: MatterArtifactTemplateSelectionRead[]
  appliedTemplates: MatterArtifactAppliedTemplateRead[]
  // The conversation's artifact scope (derived from the detail response's artifact versions).
  // Reconciliation deletes stale in-scope entries; ids outside this scope are never touched,
  // since other conversations share the template maps.
  matterArtifactIds: string[]
  matterArtifactVersionIds: string[]
  dispatch: AppDispatch
}

/**
 * Reconcile a conversation's persisted template state into the store on load:
 *  - per-artifact selections and per-version applied records (kept by id) are upserted, and any
 *    in-scope entry absent from the response is removed — a selection cleared elsewhere must not
 *    survive a refetch as a stale Redux entry, and
 *  - the referenced template UserFile records are fetched, so the toolbar can resolve their
 *    display names — template state stores ids only, and a fresh conversation load doesn't
 *    otherwise load the files.
 *
 * The file fetch is fire-and-forget and isolated: the name fills in once it resolves, and a failure
 * there must never fail conversation loading.
 */
export function hydrateAgentV2ConversationTemplateState({
  selections,
  appliedTemplates,
  matterArtifactIds,
  matterArtifactVersionIds,
  dispatch,
}: HydrateAgentV2ConversationTemplateStateArgs): void {
  dispatch(ArtifactTemplatesActions.reconcileMatterArtifactTemplateState({ matterArtifactIds, matterArtifactVersionIds, selections, appliedTemplates }))

  const templateFileIds = [
    ...selections.map((selection) => selection.template_user_file_id),
    ...appliedTemplates.map((applied) => applied.template_user_file_id),
  ].filter((id): id is string => Boolean(id))

  if (templateFileIds.length > 0) {
    fetchAndStoreUserFiles(templateFileIds, dispatch).catch(() => undefined)
  }
}
